import { Link } from "react-router-dom";
import type { Video } from "@/types/content";
import { categoryMap } from "@/mocks/categories";
import { formatDate, formatViews } from "@/lib/content";

interface VideoCardProps {
  video: Video;
  variant?: "vertical" | "horizontal";
}

export default function VideoCard({ video, variant = "vertical" }: VideoCardProps) {
  const category = categoryMap[video.category];

  if (variant === "horizontal") {
    return (
      <Link
        to={`/detail/video/${video.id}`}
        className="group flex gap-3 rounded-lg p-2 transition-colors hover:bg-background-100"
      >
        <div className="relative h-16 w-28 shrink-0 overflow-hidden rounded-md bg-background-200">
          <img
            src={video.cover}
            alt={`${video.title} - 抖阳视频内容`}
            title={`${video.title} - 抖阳视频`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
          <span className="absolute bottom-1 right-1 rounded bg-foreground-950/75 px-1 py-0.5 font-label text-[10px] text-background-50">
            {video.duration}
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="line-clamp-2-custom text-sm font-medium leading-relaxed text-foreground-900 transition-colors group-hover:text-primary-600">
            {video.title}
          </h3>
          <p className="mt-1.5 text-xs text-foreground-400">
            {category?.name} · {formatViews(video.views)} 次播放
          </p>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/video/${video.id}`}
      className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-200"
    >
      <div className="relative aspect-[16/10] w-full overflow-hidden bg-background-200">
        <img
          src={video.cover}
          alt={`${video.title} - 抖阳视频内容`}
          title={`${video.title} - 抖阳视频`}
          className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/45 via-transparent to-transparent" />
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-background-50/90 text-primary-600 transition-transform duration-300 group-hover:scale-110">
            <i className="ri-play-fill text-xl" />
          </span>
        </span>
        <span className="absolute bottom-2.5 right-3 rounded bg-foreground-950/70 px-1.5 py-0.5 font-label text-[11px] text-background-50">
          {video.duration}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <h3 className="text-sm font-semibold leading-relaxed text-foreground-950 transition-colors group-hover:text-primary-700 md:text-base">
          {video.title}
        </h3>
        <p className="mt-2 line-clamp-2-custom text-xs leading-relaxed text-foreground-500">{video.summary}</p>
        <div className="mt-auto flex items-center justify-between gap-2 pt-3 text-xs text-foreground-400">
          <span>{category?.name}</span>
          <span className="flex items-center gap-2">
            <span>{formatDate(video.date)}</span>
            <span className="text-background-400">|</span>
            <span className="flex items-center gap-1">
              <i className="ri-play-circle-line" />
              {formatViews(video.views)}
            </span>
          </span>
        </div>
      </div>
    </Link>
  );
}